'use client';
import React, { useState, useMemo } from 'react';
import AnnouncementBar from '@/components/AnnouncementBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// --- Types ---
interface BookingForm {
  name: string;
  email: string;
  phone: string;
  notes: string;
}

// --- Dummy booked slots per weekday (0=Mon … 6=Sun) ---
// We generate a deterministic set of "booked" slots based on the day-of-month
function getBookedSlotsForDate(date: Date): number[] {
  const seed = date.getDate() + date.getMonth() * 31;
  const allSlots = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]; // indices into TIME_SLOTS
  // Pick 3-4 slots deterministically
  const count = (seed % 2 === 0) ? 3 : 4;
  const booked: number[] = [];
  for (let i = 0; i < count; i++) {
    booked.push(allSlots[(seed * (i + 3) + i * 7) % allSlots.length]);
  }
  return [...new Set(booked)];
}

const TIME_SLOTS = [
  '8:00 – 9:00 am',
  '9:00 – 10:00 am',
  '10:00 – 11:00 am',
  '11:00 am – 12:00 pm',
  '12:00 – 1:00 pm',
  '1:00 – 2:00 pm',
  '2:00 – 3:00 pm',
  '3:00 – 4:00 pm',
  '4:00 – 5:00 pm',
  '5:00 – 6:00 pm',
  '6:00 – 7:00 pm',
  '7:00 – 8:00 pm',
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function isSunday(date: Date) {
  return date.getDay() === 0;
}

function isPast(date: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

function isToday(date: Date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function formatDate(date: Date) {
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

// --- Calendar Component ---
function Calendar({
  selectedDate,
  onSelectDate,
}: {
  selectedDate: Date | null;
  onSelectDate: (d: Date) => void;
}) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1);
  // JS getDay(): 0=Sun, 1=Mon … 6=Sat → convert to Mon-first
  const startDow = (firstDay.getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  // Disable "prev" if we're already on the current month
  const canGoPrev = !(viewYear === today.getFullYear() && viewMonth === today.getMonth());

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          disabled={!canGoPrev}
          className="w-8 h-8 flex items-center justify-center transition-opacity"
          style={{
            fontFamily: 'adobe-garamond-pro, Georgia, serif',
            fontSize: '18px',
            opacity: canGoPrev ? 1 : 0.25,
            cursor: canGoPrev ? 'pointer' : 'default',
          }}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span
          style={{
            fontFamily: 'orpheus-pro, Georgia, serif',
            fontSize: '17px',
            fontWeight: '400',
            color: '#000',
          }}
        >
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          onClick={nextMonth}
          className="w-8 h-8 flex items-center justify-center"
          style={{ fontFamily: 'adobe-garamond-pro, Georgia, serif', fontSize: '18px', cursor: 'pointer' }}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => (
          <div
            key={d}
            className="text-center py-1"
            style={{
              fontFamily: 'adobe-garamond-pro, Georgia, serif',
              fontSize: '12px',
              color: 'rgba(0,0,0,0.45)',
              letterSpacing: '0.06em',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Date cells */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((date, idx) => {
          if (!date) return <div key={`empty-${idx}`} />;
          const disabled = isPast(date) || isSunday(date);
          const selected =
            selectedDate &&
            date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear();
          const todayCell = isToday(date);

          return (
            <button
              key={date.toISOString()}
              disabled={disabled}
              onClick={() => !disabled && onSelectDate(date)}
              className="mx-auto flex items-center justify-center rounded-full transition-all duration-150"
              style={{
                width: '36px',
                height: '36px',
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '15px',
                cursor: disabled ? 'default' : 'pointer',
                backgroundColor: selected ? '#000' : todayCell && !selected ? '#EFE3E1' : 'transparent',
                color: selected ? '#EFE3E1' : disabled ? 'rgba(0,0,0,0.25)' : '#000',
                border: 'none',
                fontWeight: todayCell ? '700' : '400',
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- Main Page ---
export default function ContactPage() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [form, setForm] = useState<BookingForm>({ name: '', email: '', phone: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const bookedSlots = useMemo(
    () => (selectedDate ? getBookedSlotsForDate(selectedDate) : []),
    [selectedDate]
  );

  function handleDateSelect(date: Date) {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSubmitted(false);
  }

  function handleSlotSelect(idx: number) {
    if (!bookedSlots.includes(idx)) {
      setSelectedSlot(idx);
      setSubmitted(false);
    }
  }

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedDate || selectedSlot === null) {
      setFormError('Please select a date and time slot before submitting.');
      return;
    }
    if (!form.name.trim() || !form.email.trim()) {
      setFormError('Please enter your full name and email address.');
      return;
    }
    const bookingData = {
      date: formatDate(selectedDate),
      slot: TIME_SLOTS[selectedSlot],
      ...form,
    };
    console.log('Booking request:', bookingData);
    setSubmitted(true);
    setFormError('');
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EFE3E1' }}>
      <AnnouncementBar />
      <Header />

      <main>
        {/* ── 1. Page Header ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#EFE3E1', paddingTop: '5vw', paddingBottom: '5vw' }}
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h1
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 60px)',
                fontWeight: '400',
                fontStyle: 'normal',
                color: '#000000',
                lineHeight: '1.2',
              }}
            >
              Get in Touch
            </h1>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(15px, 1.2vw, 20px)',
                fontWeight: '400',
                color: '#000000',
                lineHeight: '1.7',
                maxWidth: '640px',
                margin: '20px auto 0',
              }}
            >
              Reach out to arrange a consultation, or book an online session directly below.
            </p>
          </div>
        </section>

        {/* ── 2 & 3. Two-column layout ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#EFE3E1', paddingTop: '5vw', paddingBottom: '6vw' }}
        >
          <div
            className="mx-auto px-6 md:px-12"
            style={{ maxWidth: '1200px' }}
          >
            <div className="flex flex-col lg:flex-row gap-12 xl:gap-16 items-start">

              {/* ── Left: Practitioner Details Card ── */}
              <div className="w-full lg:w-5/12 flex-shrink-0">
                <div
                  className="p-8 md:p-10"
                  style={{ backgroundColor: '#EFE3E1' }}
                >
                  {/* Name & credentials */}
                  <h2
                    style={{
                      fontFamily: 'orpheus-pro, Georgia, serif',
                      fontSize: 'clamp(22px, 2vw, 32px)',
                      fontWeight: '400',
                      color: '#000',
                      lineHeight: '1.2',
                      marginBottom: '6px',
                    }}
                  >
                    Dr Jennifer Morgan
                  </h2>
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '14px',
                      color: 'rgba(0,0,0,0.6)',
                      letterSpacing: '0.03em',
                      marginBottom: '28px',
                    }}
                  >
                    BA Hons; MSc; DClinPsych(Oxon); DipCAT; CPsychol
                  </p>

                  {/* Divider */}
                  <div style={{ height: '1px', backgroundColor: 'rgba(0,0,0,0.15)', marginBottom: '28px' }} />

                  {/* Contact links */}
                  <div className="flex flex-col gap-4" style={{ marginBottom: '32px' }}>
                    {/* Email */}
                    <a
                      href="mailto:info@drjmorgan.co.uk"
                      className="flex items-center gap-3 group"
                      style={{ textDecoration: 'none' }}
                    >
                      <span
                        className="flex-shrink-0 flex items-center justify-center"
                        style={{
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#000',
                          borderRadius: '50%',
                        }}
                      >
                        {/* Envelope icon */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EFE3E1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="M2 7l10 7 10-7" />
                        </svg>
                      </span>
                      <span
                        style={{
                          fontFamily: 'adobe-garamond-pro, Georgia, serif',
                          fontSize: '16px',
                          color: '#000',
                          borderBottom: '1px solid rgba(0,0,0,0.25)',
                          paddingBottom: '1px',
                          transition: 'border-color 0.2s',
                        }}
                      >
                        info@drjmorgan.co.uk
                      </span>
                    </a>

                    {/* Phone removed — email contact preferred */}
                  </div>

                  {/* Divider */}
                  <div style={{ height: '1px', backgroundColor: 'rgba(0,0,0,0.15)', marginBottom: '28px' }} />

                  {/* Location */}
                  <div>
                    <h3
                      style={{
                        fontFamily: 'orpheus-pro, Georgia, serif',
                        fontSize: '16px',
                        fontWeight: '400',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#000',
                        marginBottom: '10px',
                      }}
                    >
                      Location
                    </h3>
                    <p
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: '15px',
                        color: 'rgba(0,0,0,0.75)',
                        lineHeight: '1.75',
                      }}
                    >
                      Based in Wales and available throughout the world via online sessions. Please contact Dr Morgan directly to arrange face-to-face or online appointments.
                    </p>
                    <div style={{ marginTop: '20px' }}>
                      <img
                        src="/assets/images/image-1784372585164.png"
                        alt="BPS Chartered Psychologist and HCPC Registered logos"
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Right: Booking Widget ── */}
              <div className="w-full lg:flex-1">
                <h2
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(20px, 2vw, 30px)',
                    fontWeight: '400',
                    color: '#000',
                    marginBottom: '8px',
                  }}
                >
                  Book an Online Consultation
                </h2>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '15px',
                    color: 'rgba(0,0,0,0.6)',
                    lineHeight: '1.6',
                    marginBottom: '28px',
                  }}
                >
                  Select a date and one-hour time slot below. All times shown are UK time (GMT/BST).
                </p>

                {/* Calendar */}
                <div
                  className="p-6 md:p-8"
                  style={{
                    border: '1px solid rgba(0,0,0,0.1)',
                    marginBottom: '24px',
                  }}
                >
                  <Calendar selectedDate={selectedDate} onSelectDate={handleDateSelect} />
                </div>

                {/* Time slots — only show when date selected */}
                {selectedDate && (
                  <div style={{ marginBottom: '28px' }}>
                    <p
                      style={{
                        fontFamily: 'orpheus-pro, Georgia, serif',
                        fontSize: '15px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#000',
                        marginBottom: '14px',
                      }}
                    >
                      Available times — {formatDate(selectedDate)}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {TIME_SLOTS.map((slot, idx) => {
                        const booked = bookedSlots.includes(idx);
                        const active = selectedSlot === idx;
                        return (
                          <button
                            key={slot}
                            disabled={booked}
                            onClick={() => handleSlotSelect(idx)}
                            style={{
                              fontFamily: 'adobe-garamond-pro, Georgia, serif',
                              fontSize: '14px',
                              padding: '10px 8px',
                              border: active ? '1px solid #000' : '1px solid rgba(0,0,0,0.18)',
                              backgroundColor: active ? '#000' : booked ? 'rgba(0,0,0,0.04)' : '#EFE3E1',
                              color: active ? '#EFE3E1' : booked ? 'rgba(0,0,0,0.3)' : '#000',
                              cursor: booked ? 'default' : 'pointer',
                              transition: 'all 0.15s ease',
                              textAlign: 'center',
                              lineHeight: '1.3',
                            }}
                          >
                            {booked ? (
                              <span>
                                <span style={{ display: 'block', fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '2px' }}>Booked</span>
                                {slot}
                              </span>
                            ) : slot}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Booking form — show when slot selected */}
                {selectedDate && selectedSlot !== null && !submitted && (
                  <form onSubmit={handleSubmit} style={{ marginBottom: '16px' }}>
                    <div
                      style={{
                        height: '1px',
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        marginBottom: '24px',
                      }}
                    />
                    <p
                      style={{
                        fontFamily: 'orpheus-pro, Georgia, serif',
                        fontSize: '15px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#000',
                        marginBottom: '18px',
                      }}
                    >
                      Your Details
                    </p>

                    <div className="flex flex-col gap-4">
                      {/* Full Name */}
                      <div>
                        <label
                          htmlFor="name"
                          style={{
                            fontFamily: 'adobe-garamond-pro, Georgia, serif',
                            fontSize: '13px',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'rgba(0,0,0,0.55)',
                            display: 'block',
                            marginBottom: '6px',
                          }}
                        >
                          Full Name *
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={form.name}
                          onChange={handleFormChange}
                          className="newsletter-input"
                          placeholder="Your full name"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label
                          htmlFor="email"
                          style={{
                            fontFamily: 'adobe-garamond-pro, Georgia, serif',
                            fontSize: '13px',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'rgba(0,0,0,0.55)',
                            display: 'block',
                            marginBottom: '6px',
                          }}
                        >
                          Email Address *
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={handleFormChange}
                          className="newsletter-input"
                          placeholder="your@email.com"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label
                          htmlFor="phone"
                          style={{
                            fontFamily: 'adobe-garamond-pro, Georgia, serif',
                            fontSize: '13px',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'rgba(0,0,0,0.55)',
                            display: 'block',
                            marginBottom: '6px',
                          }}
                        >
                          Phone Number
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleFormChange}
                          className="newsletter-input"
                          placeholder="e.g. 07700 900 000"
                        />
                      </div>

                      {/* Notes */}
                      <div>
                        <label
                          htmlFor="notes"
                          style={{
                            fontFamily: 'adobe-garamond-pro, Georgia, serif',
                            fontSize: '13px',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'rgba(0,0,0,0.55)',
                            display: 'block',
                            marginBottom: '6px',
                          }}
                        >
                          Reason for consultation / anything you&apos;d like Dr Morgan to know{' '}
                          <span style={{ textTransform: 'none', letterSpacing: 0, color: 'rgba(0,0,0,0.35)' }}>(optional)</span>
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          rows={4}
                          value={form.notes}
                          onChange={handleFormChange}
                          className="newsletter-input"
                          placeholder="Share anything that might help Dr Morgan prepare for your session…"
                          style={{ resize: 'vertical' }}
                        />
                      </div>

                      {/* Error */}
                      {formError && (
                        <p
                          style={{
                            fontFamily: 'adobe-garamond-pro, Georgia, serif',
                            fontSize: '14px',
                            color: '#8b0000',
                          }}
                        >
                          {formError}
                        </p>
                      )}

                      {/* Submit */}
                      <div>
                        <button
                          type="submit"
                          className="site-btn-primary"
                          style={{ width: '100%', textAlign: 'center' }}
                        >
                          Request Booking
                        </button>
                      </div>

                      {/* Disclaimer */}
                      <p
                        style={{
                          fontFamily: 'adobe-garamond-pro, Georgia, serif',
                          fontSize: '13px',
                          color: 'rgba(0,0,0,0.45)',
                          lineHeight: '1.6',
                          fontStyle: 'normal',
                        }}
                      >
                        This is a booking request, not a confirmed appointment, until you receive email confirmation.
                      </p>
                    </div>
                  </form>
                )}

                {/* Confirmation state */}
                {submitted && selectedDate && selectedSlot !== null && (
                  <div
                    className="p-8"
                    style={{
                      backgroundColor: '#EFE3E1',
                      marginBottom: '16px',
                    }}
                  >
                    <div
                      className="flex items-center justify-center mb-4"
                      style={{
                        width: '44px',
                        height: '44px',
                        backgroundColor: '#000',
                        borderRadius: '50%',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EFE3E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <h3
                      style={{
                        fontFamily: 'orpheus-pro, Georgia, serif',
                        fontSize: 'clamp(18px, 1.5vw, 24px)',
                        fontWeight: '400',
                        color: '#000',
                        marginBottom: '12px',
                        lineHeight: '1.3',
                      }}
                    >
                      Thank you — your request has been received.
                    </h3>
                    <p
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: '16px',
                        color: 'rgba(0,0,0,0.75)',
                        lineHeight: '1.7',
                        marginBottom: '16px',
                      }}
                    >
                      Your request for <strong>{formatDate(selectedDate)}</strong> at{' '}
                      <strong>{TIME_SLOTS[selectedSlot]}</strong> has been received. Dr Morgan will confirm by email within 24 hours.
                    </p>
                    <p
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: '13px',
                        color: 'rgba(0,0,0,0.45)',
                        lineHeight: '1.6',
                        fontStyle: 'normal',
                      }}
                    >
                      This is a booking request, not a confirmed appointment, until you receive email confirmation.
                    </p>
                  </div>
                )}

                {/* Prompt to select date/slot if form not yet visible */}
                {!selectedDate && (
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '15px',
                      color: 'rgba(0,0,0,0.45)',
                      fontStyle: 'normal',
                    }}
                  >
                    Please select a date from the calendar above to view available time slots.
                  </p>
                )}
                {selectedDate && selectedSlot === null && !submitted && (
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '15px',
                      color: 'rgba(0,0,0,0.45)',
                      fontStyle: 'normal',
                    }}
                  >
                    Please select a time slot above to continue.
                  </p>
                )}

                {/* Client note about Sundays */}
                <div
                  className="mt-8 p-5"
                  style={{
                    border: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(239,227,225,0.3)',
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '13px',
                      color: 'rgba(0,0,0,0.5)',
                      lineHeight: '1.65',
                      fontStyle: 'normal',
                    }}
                  >
                    <strong style={{ fontStyle: 'normal', color: 'rgba(0,0,0,0.65)' }}>Note for Dr Morgan:</strong> Sundays have been disabled in the booking calendar on the assumption that you do not work on Sundays. Please confirm whether this is correct, and whether any other days or hours should be restricted.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
